import { useCallback, useEffect, useRef, useState } from "react";
import { getMessagesService } from "../../../../infrastructure/services/NotificationsService";
import { mapChatConversationPreview } from "../../../../infrastructure/mappers/ChatConversationMapper";
import { hasUnreadMessages } from "../../../components/utils/messageUnread";

const DEFAULT_COUNTERS = {
  all: 0,
  unread: 0,
  archived: 0,
};

const DEFAULT_PROFILE_IMAGE = "/img/icons/UserFalse.svg";

const shouldKeepConversation = (contact, activeFilter) => {
  if (activeFilter === "archived") return Boolean(contact.isArchived);
  if (activeFilter === "unread")
    return !contact.isArchived && hasUnreadMessages(contact);
  if (activeFilter === "read")
    return !contact.isArchived && !hasUnreadMessages(contact);
  return !contact.isArchived;
};

const getTimestampValue = (contact) => {
  const value = new Date(contact?.timestamp ?? contact?.fechaEnvio ?? 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

const consolidateContactsByUser = (contacts = []) => {
  const byUser = new Map();

  contacts.forEach((contact) => {
    const userKey = String(contact?.id ?? contact?.idUsuarioOrigen ?? "");
    const fallbackKey = `chat:${contact?.idChat ?? Math.random()}`;
    const key = userKey || fallbackKey;
    const existing = byUser.get(key);

    if (!existing) {
      byUser.set(key, {
        ...contact,
        chatIds: contact?.idChat ? [contact.idChat] : [],
      });
      return;
    }

    if (getTimestampValue(contact) >= getTimestampValue(existing)) {
      byUser.set(key, {
        ...contact,
        chatIds: contact?.idChat ? [contact.idChat] : [],
      });
    }
  });

  return Array.from(byUser.values()).sort(
    (a, b) => getTimestampValue(b) - getTimestampValue(a),
  );
};

const normalizeContactsByFilter = (messages = [], activeFilter) =>
  consolidateContactsByUser((messages || []).map((message) => {
    const contact = mapChatConversationPreview(message);

    if (activeFilter === "archived") {
      return {
        ...contact,
        isArchived: true,
        isRead: true,
        unreadCount: 0,
        status: "archived",
      };
    }

    if (activeFilter === "unread") {
      return {
        ...contact,
        isArchived: false,
        unreadCount: Math.max(1, Number(contact.unreadCount || 0)),
        status: "unread",
      };
    }

    if (activeFilter === "read") {
      return {
        ...contact,
        isArchived: false,
        unreadCount: 0,
        isRead: true,
        status: "read",
      };
    }

    return {
      ...contact,
      isArchived: false,
    };
  }));

const normalizeContactsWithProfileUrl = (contacts = []) =>
  contacts.map((contact) => {
    const fotoPerfilUrl =
      contact.fotoPerfilUrl ?? contact.avatar ?? DEFAULT_PROFILE_IMAGE;

    return {
      ...contact,
      fotoPerfilUrl,
      avatar: fotoPerfilUrl,
    };
  });

export const useChatConversations = (
  userId,
  activeFilter = "all",
  activeContactId = null,
) => {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [isRefreshingContacts, setIsRefreshingContacts] = useState(false);
  const [counters, setCounters] = useState(DEFAULT_COUNTERS);
  const hasLoadedContactsRef = useRef(false);
  const lastUserIdRef = useRef(userId);
  const contactsRef = useRef([]);
  const realtimeRefreshTimeoutRef = useRef(null);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  const fetchCounters = useCallback(async () => {
    if (!userId) {
      setCounters(DEFAULT_COUNTERS);
      return;
    }

    try {
      const [allChats, archivedChats] = await Promise.all([
        getMessagesService(userId, { mostrarArchivados: false }),
        getMessagesService(userId, { mostrarArchivados: true }),
      ]);

      const allContacts = normalizeContactsByFilter(allChats, "all");
      const archivedContacts = normalizeContactsByFilter(
        archivedChats,
        "archived",
      );

      setCounters({
        all: allContacts.length,
        unread: allContacts.filter(
          (contact) =>
            String(contact.id ?? "") !== String(activeContactId ?? "") &&
            hasUnreadMessages(contact),
        ).length,
        archived: archivedContacts.length,
      });
    } catch (error) {
      console.error("Error fetching chat counters:", error);
      setCounters(DEFAULT_COUNTERS);
    }
  }, [activeContactId, userId]);

  const fetchContacts = useCallback(async () => {
    if (!userId) {
      setContacts([]);
      setLoadingContacts(false);
      setIsRefreshingContacts(false);
      hasLoadedContactsRef.current = false;
      return;
    }

    const shouldShowInitialLoading = !hasLoadedContactsRef.current;

    if (shouldShowInitialLoading) {
      setLoadingContacts(true);
      setIsRefreshingContacts(false);
    } else {
      setIsRefreshingContacts(true);
    }

    try {
      const isArchivedFilter = activeFilter === "archived";
      const filters = { mostrarArchivados: isArchivedFilter };

      // Para all/read/unread se obtiene una sola lista activa y el filtrado se
      // realiza en cliente. Asi evitamos una llamada distinta por EstatusMensaje.
      const messages = await getMessagesService(userId, filters);
      const normalizedContacts = normalizeContactsByFilter(
        messages,
        isArchivedFilter ? "archived" : "all",
      ).map((contact) => {
        if (String(contact.id ?? "") !== String(activeContactId ?? "")) {
          return contact;
        }

        return {
          ...contact,
          unreadCount: 0,
          isRead: true,
          status: contact.isArchived ? "archived" : "read",
        };
      });
      const enrichedContacts = normalizeContactsWithProfileUrl(normalizedContacts);
      setContacts(
        enrichedContacts.filter((contact) =>
          shouldKeepConversation(contact, activeFilter),
        ),
      );
      hasLoadedContactsRef.current = true;
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoadingContacts(false);
      setIsRefreshingContacts(false);
    }
  }, [activeContactId, activeFilter, userId]);

  useEffect(() => {
    if (lastUserIdRef.current !== userId) {
      hasLoadedContactsRef.current = false;
      lastUserIdRef.current = userId;
    }
  }, [userId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchCounters();
  }, [fetchCounters]);

  useEffect(() => {
    if (!userId) return;

    const handleMessagesListUpdated = () => {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
      realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
        fetchContacts();
        fetchCounters();
      }, 250);
    };

    const realtimeEvents = [
      "chat:realtime-message",
      "chat:realtime-reaction",
      "chat:realtime-state-change",
    ];

    realtimeEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleMessagesListUpdated);
    });

    return () => {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
      realtimeEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleMessagesListUpdated);
      });
    };
  }, [fetchContacts, fetchCounters, userId]);

  const updateContactPreview = useCallback(
    (contactId, text, timestamp, options = {}) => {
      const {
        incrementUnread = false,
        markAsRead = false,
        lastMessageId = null,
      } = options;

      setContacts((prevContacts) => {
        const updatedContacts = [...prevContacts];
        const contactIndex = updatedContacts.findIndex((contact) => {
          return String(contact.id ?? "") === String(contactId ?? "");
        });

        if (contactIndex === -1) return prevContacts;

        const currentContact = updatedContacts[contactIndex];
        const nextUnreadCount = markAsRead
          ? 0
          : incrementUnread
            ? (currentContact.unreadCount || 0) + 1
            : currentContact.unreadCount || 0;

        updatedContacts[contactIndex] = {
          ...currentContact,
          lastMessage: text?.length > 40 ? `${text.slice(0, 30)}...` : text,
          lastMessageId: lastMessageId ?? currentContact.lastMessageId,
          timestamp,
          unreadCount: nextUnreadCount,
          isRead: nextUnreadCount === 0,
          status: currentContact.isArchived
            ? "archived"
            : nextUnreadCount > 0
              ? "unread"
              : "read",
        };

        const nextContact = updatedContacts[contactIndex];

        if (!shouldKeepConversation(nextContact, activeFilter)) {
          updatedContacts.splice(contactIndex, 1);
          return updatedContacts;
        }

        const [contact] = updatedContacts.splice(contactIndex, 1);
        updatedContacts.unshift(contact);

        return updatedContacts;
      });

      setCounters((prevCounters) => {
        const currentContact = contacts.find((contact) => {
          return String(contact.id ?? "") === String(contactId ?? "");
        });

        if (!currentContact || currentContact.isArchived) {
          return prevCounters;
        }

        const wasUnread = (currentContact.unreadCount || 0) > 0;
        const willBeUnread = markAsRead
          ? false
          : incrementUnread
            ? true
            : wasUnread;

        if (wasUnread === willBeUnread) {
          return prevCounters;
        }

        return {
          ...prevCounters,
          unread: Math.max(0, prevCounters.unread + (willBeUnread ? 1 : -1)),
        };
      });
    },
    [activeFilter, contacts],
  );

  const removeConversation = useCallback(
    (idChat) => {
      const targetContact = contacts.find(
        (contact) => String(contact.idChat ?? "") === String(idChat ?? ""),
      );

      setContacts((prevContacts) =>
        prevContacts.filter(
          (contact) => String(contact.idChat ?? "") !== String(idChat ?? ""),
        ),
      );

      setCounters((prevCounters) => ({
        ...prevCounters,
        all: Math.max(
          0,
          targetContact && !targetContact.isArchived
            ? prevCounters.all - 1
            : prevCounters.all,
        ),
        unread: Math.max(
          0,
          targetContact &&
            !targetContact.isArchived &&
            (targetContact.unreadCount || 0) > 0
            ? prevCounters.unread - 1
            : prevCounters.unread,
        ),
        archived: Math.max(
          0,
          targetContact && targetContact.isArchived
            ? prevCounters.archived - 1
            : prevCounters.archived,
        ),
      }));
    },
    [contacts],
  );

  const archiveConversation = useCallback(
    (idChat) => {
      const targetContact = contacts.find(
        (contact) => String(contact.idChat ?? "") === String(idChat ?? ""),
      );

      setContacts((prevContacts) => {
        const nextContacts = prevContacts
          .map((contact) => {
            if (String(contact.idChat ?? "") !== String(idChat ?? "")) {
              return contact;
            }

            return {
              ...contact,
              isArchived: true,
              isRead: true,
              unreadCount: 0,
              status: "archived",
            };
          })
          .filter((contact) => shouldKeepConversation(contact, activeFilter));

        return nextContacts;
      });

      setCounters((prevCounters) => ({
        ...prevCounters,
        all: Math.max(
          0,
          targetContact && !targetContact.isArchived
            ? prevCounters.all - 1
            : prevCounters.all,
        ),
        unread: Math.max(
          0,
          targetContact &&
            !targetContact.isArchived &&
            (targetContact.unreadCount || 0) > 0
            ? prevCounters.unread - 1
            : prevCounters.unread,
        ),
        archived:
          targetContact && !targetContact.isArchived
            ? prevCounters.archived + 1
            : prevCounters.archived,
      }));
    },
    [activeFilter, contacts],
  );

  const markConversationAsRead = useCallback(
    (contactId) => {
      const currentContact = contactsRef.current.find((contact) => {
        return String(contact.id ?? "") === String(contactId ?? "");
      });
      const wasUnread = (currentContact?.unreadCount || 0) > 0;

      setContacts((prevContacts) =>
        prevContacts
          .map((contact) => {
            if (String(contact.id ?? "") !== String(contactId ?? "")) {
              return contact;
            }

            return {
              ...contact,
              unreadCount: 0,
              isRead: true,
              status: contact.isArchived ? "archived" : "read",
            };
          })
          .filter((contact) => shouldKeepConversation(contact, activeFilter)),
      );

      if (wasUnread && !currentContact?.isArchived) {
        setCounters((prevCounters) => ({
          ...prevCounters,
          unread: Math.max(0, prevCounters.unread - 1),
        }));
      }
    },
    [activeFilter],
  );

  return {
    contacts,
    counters,
    loadingContacts,
    isRefreshingContacts,
    fetchContacts,
    fetchCounters,
    updateContactPreview,
    markConversationAsRead,
    removeConversation,
    archiveConversation,
  };
};
